/**
 * HeartRuGrandma · public one-tap Russian heart-healing for Baba Olya.
 *
 * Flow:
 *   1. Landing card → «Начать» button
 *   2. Hand-off to HeartImmersionRu — the SAME healing session engine
 *      that powers Heart Flow & Warmth in the app (3 silent breaths →
 *      5-min immersion with HeartVisualEngine → dissolve). Only the
 *      voice track (Mia's Russian master mix) and on-screen text are
 *      localised. This is an actual healing session, not a visual.
 *   3. Completion card → «Ещё раз»
 *
 * No login · no chrome · no photos of anyone · WhatsApp-shareable.
 * Apple Touch Icon injected so she can pin the page to Home Screen.
 */
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import HeartImmersionRu from "./HeartImmersionRu";

export default function HeartRuGrandma() {
  const [phase, setPhase] = useState("idle"); // idle | session | complete
  const cleanupRef = useRef([]);

  // Immersive body flag + Apple Touch Icon meta tags — set on mount.
  // Grandma pinning to Home Screen sees a golden heart labelled «Мия».
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.dataset.immersive = 'true';
    const prevTitle = document.title;
    document.title = 'Мия · сердце';

    const added = [];
    const setMeta = (attrs) => {
      const el = document.createElement('meta');
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      added.push(el);
    };
    const setLink = (attrs) => {
      const el = document.createElement('link');
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      added.push(el);
    };
    setMeta({ name: 'apple-mobile-web-app-capable',          content: 'yes' });
    setMeta({ name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' });
    setMeta({ name: 'apple-mobile-web-app-title',            content: 'Мия' });
    setMeta({ name: 'theme-color',                           content: '#1a0a0f' });
    setLink({ rel: 'apple-touch-icon',                       href: '/apple-touch-icon.png' });
    setLink({ rel: 'icon', type: 'image/png', sizes: '512x512', href: '/heart-ru-512.png' });

    return () => {
      delete document.body.dataset.immersive;
      document.title = prevTitle;
      added.forEach((el) => { try { el.remove(); } catch (_) { /* noop */ } });
      cleanupRef.current.forEach((fn) => { try { fn(); } catch (_) { /* noop */ } });
    };
  }, []);

  return (
    <div
      data-testid="heart-ru-grandma"
      className="fixed inset-0 overflow-hidden"
      style={{ background: "radial-gradient(circle at 50% 50%, #1a0a0f 0%, #0a0508 55%, #050205 100%)" }}
    >
      <AnimatePresence mode="wait">
        {phase === "idle" && (
          <IdleScreen key="idle" onStart={() => setPhase("session")} />
        )}
        {phase === "session" && (
          <motion.div
            key="session"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="fixed inset-0"
          >
            <HeartImmersionRu onComplete={() => setPhase("complete")} />
          </motion.div>
        )}
        {phase === "complete" && (
          <CompleteScreen key="complete" onRestart={() => setPhase("idle")} />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Idle · «Начать» ─────────────────────────────────────────── */
function IdleScreen({ onStart }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-10 flex flex-col items-center justify-center gap-10 px-8 text-center min-h-screen"
    >
      {/* Ambient breathing background — very soft warmth so text reads clean */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 55%, rgba(180, 90, 60, 0.18) 0%, transparent 60%)',
        }}
        animate={{ opacity: [0.7, 0.95, 0.7] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative flex flex-col items-center gap-3">
        <div
          className="text-amber-100/85 tracking-[0.35em] uppercase text-[10px]"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Reclaim  ·  Ателье Исцеления
        </div>
        <div
          className="text-amber-50 text-4xl sm:text-5xl leading-tight"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', letterSpacing: '0.01em' }}
        >
          Здравствуй,
          <br/>
          <span className="text-rose-200/95 italic">баба Оля</span>
        </div>
        <div
          className="text-white/55 text-sm max-w-xs mx-auto italic mt-3"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Настройся, вдыхай целительную энергию,
          <br/>позволь свету наполнить твоё сердце
        </div>
      </div>

      <motion.button
        onClick={onStart}
        data-testid="heart-ru-start"
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className="relative flex items-center justify-center rounded-full"
        style={{
          width: 220, height: 220,
          background: 'radial-gradient(circle at 50% 45%, #f6d68a 0%, #d59f4f 45%, #8a5b26 100%)',
          boxShadow: '0 0 60px rgba(245, 200, 130, 0.55), 0 0 120px rgba(200, 130, 80, 0.35), inset 0 6px 24px rgba(255, 240, 200, 0.5), inset 0 -6px 24px rgba(120, 60, 30, 0.5)',
        }}
      >
        <motion.span
          className="absolute inset-0 rounded-full"
          style={{ border: '1px solid rgba(255, 224, 170, 0.55)' }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.6, 0.15, 0.6] }}
          transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span
          className="text-[#3a1e08] text-3xl tracking-wide"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 600, letterSpacing: '0.03em' }}
        >
          Начать
        </span>
      </motion.button>

      <div className="text-white/40 text-xs italic tracking-widest">
        5 минут · с наушниками лучше
      </div>
    </motion.div>
  );
}

/* ── Complete · softness afterward ────────────────────────────── */
function CompleteScreen({ onRestart }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-10 flex flex-col items-center justify-center gap-8 px-8 text-center min-h-screen"
    >
      <div className="flex flex-col items-center gap-3">
        <div
          className="text-amber-50 text-3xl sm:text-4xl italic leading-tight"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Мы тебя любим,
          <br/>
          <span className="text-rose-200/90">баба Оля</span>
        </div>
        <div
          className="text-white/50 text-sm max-w-xs mx-auto italic mt-2"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Отдыхай. Свет остаётся с тобой.
        </div>
      </div>
      <motion.button
        onClick={onRestart}
        data-testid="heart-ru-restart"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="text-amber-100/80 text-sm tracking-[0.25em] uppercase border-b border-amber-100/25 pb-1 hover:text-amber-100"
      >
        Ещё раз
      </motion.button>
    </motion.div>
  );
}
